import { AlertTriangle, Clock, Loader2, Sparkles } from 'lucide-react';
import { CodeBlock, Markdown } from '@/components/code/CodeBlock';
import { THINKING_MESSAGES } from './thinking';
import type { Message } from './types';

function AssistantAvatar() {
  return (
    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sl-yellow text-sl-charcoal" aria-hidden>
      <Sparkles className="h-4 w-4" />
    </div>
  );
}

/** Answer text without the SQL block when the SQL is shown separately (avoids duplication). */
function answerText(message: Message) {
  return message.sql && message.role === 'assistant'
    ? message.content.replace(/```sql[\s\S]*?```/g, '').replace(/SQL QUERY:\s*/g, '').trim()
    : message.content;
}

export function ChatMessage({ message }: { message: Message }) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap text-primary-foreground shadow-card sm:max-w-[75%]">
          {message.content}
        </div>
      </div>
    );
  }

  const text = answerText(message);
  // The client stores send failures as "Error: <msg>"; show those only in the error box.
  const showText = text.length > 0 && !(message.error && text === `Error: ${message.error}`);

  return (
    <div className="flex gap-3">
      <AssistantAvatar />
      <div className="min-w-0 flex-1 space-y-3">
        {showText && (
          <div className="rounded-2xl rounded-tl-sm border border-border bg-card px-4 py-3 shadow-card">
            <Markdown>{text}</Markdown>
          </div>
        )}

        {message.sql && (
          <CodeBlock code={message.sql} language="sql" title="Gegenereerde SQL" className="bg-card shadow-card" maxHeight={360} />
        )}

        {message.error && (
          <div className="flex items-start gap-2 rounded-md border border-[#F6C4C6] bg-[#FDECEC] px-3 py-2 text-sm text-destructive" role="alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0 break-words">
              <span className="font-semibold">Er ging iets mis: </span>
              {message.error}
            </div>
          </div>
        )}

        {message.execution_time_ms !== undefined && !message.error && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            Uitgevoerd in {(message.execution_time_ms / 1000).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} s
          </div>
        )}
      </div>
    </div>
  );
}

/** Progress text the agent streams into the pending message while it works. */
export function PendingProgress({ content }: { content: string }) {
  return (
    <div className="flex gap-3">
      <AssistantAvatar />
      <div className="min-w-0 flex-1 rounded-2xl rounded-tl-sm border border-dashed border-sl-grey-300 bg-sl-grey-50 px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">
        {content}
      </div>
    </div>
  );
}

export function ThinkingIndicator({ stage, elapsed }: { stage: number; elapsed: number }) {
  return (
    <div className="flex gap-3" role="status" aria-live="polite">
      <div className="w-8 shrink-0" />
      <div className="inline-flex items-center gap-3 rounded-full border border-border bg-card px-4 py-2 shadow-card">
        <Loader2 className="h-4 w-4 animate-spin text-sl-charcoal" style={{ animationDuration: '1.2s' }} />
        <span key={stage} className="animate-fade-in text-sm text-foreground">
          {THINKING_MESSAGES[stage] ?? THINKING_MESSAGES[0]}
        </span>
        <span className="min-w-[2.5rem] text-right text-xs tabular-nums text-muted-foreground">{elapsed}s</span>
      </div>
    </div>
  );
}
